import NoDocuments from '@g17eco/images/no-documents-in-library.svg';
import { BasicAlert } from '@g17eco/molecules/alert';
import { ColumnDef, Table } from '@g17eco/molecules/table';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { useDeleteDocumentMutation, useGetDocumentsQuery, useUpdateDocumentMutation } from '@api/documents';
import { useEffect, useMemo, useState } from 'react';
import { AiProcessingValue } from '@g17eco/types/ai';
import { DocumentItem, DocumentProcessingService } from '@g17eco/types/document';
import { DocumentFilters } from './DocumentFilters';
import { generateDocumentsFlexSearchMap } from '@features/admin-dashboard/utils';
import { Checkbox, CheckboxState } from '@g17eco/atoms/checkbox';
import { DATE, formatDateUTC } from '@utils/date';
import { Button, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { DocumentToolbar } from './DocumentToolbar';
import { documentOptions } from '@features/document-library/utils';
import { DocumentModal, DocumentViewMode } from '@features/document-library/DocumentModal';
import { generateToast, generateErrorToast } from '@g17eco/molecules/toasts';
import { allowAIProcessing } from '@utils/ai';
import { BlockingLoader } from '../../../../atoms/loader';

export type FilterParams = {
  searchText: string;
  type: string;
};

interface DocumentWithMode {
  document: DocumentItem;
  mode: DocumentViewMode;
}

const isModalOpen = (selectedDocument: DocumentWithMode | undefined): selectedDocument is DocumentWithMode => {
  if (!selectedDocument) {
    return false;
  }
  return (
    selectedDocument.mode !== DocumentViewMode.AiProcessing ||
    allowAIProcessing(selectedDocument.document)
  );
};

const isProcessing = (processingServices?: DocumentProcessingService[]): boolean => {
  return processingServices?.some((p) => Boolean(p.startedDate && !p.completedDate && !p.failedDate && !p.deletedDate)) ?? false;
};

const isCompleted = (processingServices?: DocumentProcessingService[]): boolean => {
  return processingServices?.every((p) => Boolean(p.completedDate && !p.failedDate)) ?? false
};

const isFailed = (processingServices?: DocumentProcessingService[]): boolean => {
  return processingServices?.some((p) => Boolean(p.failedDate)) ?? false;
};

export const DocumentTable = ({ initiativeId }: { initiativeId: string }) => {
  const [filters, setFilters] = useState<FilterParams>({ searchText: '', type: '' });
  const [selectedDocument, setSelectedDocument] = useState<DocumentWithMode>();
  const [selectedDocuments, setSelectedDocuments] = useState<DocumentItem[]>([]);
  const [isPolling, setIsPolling] = useState(true);
  const isDocumentSelected = (docId: string) => selectedDocuments.some((doc) => doc._id === docId);
  const { data, isFetching, isLoading, error } = useGetDocumentsQuery({ initiativeId }, { pollingInterval: isPolling ? 10000 : undefined });

  const pendingDocuments = data?.documents?.some((doc) => isProcessing(doc.processingServices)) ?? false;
  useEffect(() => {
    setIsPolling(pendingDocuments);
  }, [pendingDocuments]);

  const { documents: listDocuments = [] } = data ?? {};
  const [deleteDocument] = useDeleteDocumentMutation();
  const [updateDocument, { isLoading: isUpdating }] = useUpdateDocumentMutation();

  const columns: ColumnDef<DocumentItem>[] = [
    {
      id: 'multi-select-action',
      meta: {
        cellProps: { style: { width: 50 }, className: 'text-center' },
      },
      header: '',
      cell: (c) => {
        const document = c.row.original;
        const status = isDocumentSelected(document._id) ? CheckboxState.Checked : CheckboxState.Unchecked;
        return (
          <div className='d-flex justify-content-between'>
            {document.aiProcessing === AiProcessingValue.Exclude ? (
              <SimpleTooltip text={'AI access blocked'}>
                <div className='d-inline-flex align-items-center'>
                  <i className='fal fa-ban text-muted' aria-label='AI access blocked' />
                </div>
              </SimpleTooltip>
            ) : (
              <span />
            )}
            <Checkbox
              status={status}
              onChange={() => {
                const newSelectedDocuments =
                  status === CheckboxState.Checked
                    ? selectedDocuments.filter((q) => q._id !== document._id)
                    : [...selectedDocuments, document];

                return setSelectedDocuments(newSelectedDocuments);
              }}
            />
          </div>
        );
      },
    },
    {
      accessorKey: 'ownerSubType',
      header: 'Type',
      meta: {
        headerProps: {
          style: {
            width: 230,
          },
        },
      },
      cell: ({ row }) => {
        return (
          <div data-testid='document-type-dropdown'>
            <SelectFactory
              className='w-100 border-none'
              selectType={SelectTypes.SingleSelect}
              placeholder='Select a type'
              options={documentOptions}
              value={documentOptions.find(({ value }) => value === row.original.ownerSubType)}
              isDisabled={isUpdating}
              onChange={(op) => {
                handleUpdateDocument({ ...row.original, ownerSubType: op?.value });
              }}
              isSearchable={false}
              menuPlacement='bottom'
              isMenuPortalTargetBody
              isTransparent={true}
            />
          </div>
        );
      },
    },
    {
      header: 'File name',
      cell: ({ row }) => (
        <SimpleTooltip text={row.original.description}>
          <Button
            color='link-secondary'
            className='text-left'
            onClick={() => setSelectedDocument({ document: row.original, mode: DocumentViewMode.View })}
          >
            <span className='text-ThemeTextMedium'>{row.original.title}</span>
          </Button>
        </SimpleTooltip>
      )
    },
    { accessorKey: 'metadata.extension', header: 'Format' },
    {
      accessorKey: 'created',
      header: 'Upload date',
      cell: ({ row }) => formatDateUTC(row.original.created, DATE.YEAR_MONTH_DATE),
    },
    {
      accessorKey: 'processing',
      header: 'Analysis',
      cell: ({ row }) => {
        const getIcon = () => {
          if (isFailed(row.original.processingServices)) {
            return <i className='fal fa-exclamation-triangle text-danger' />;
          }
          if (isProcessing(row.original.processingServices)) {
            return <i className='fal fa-spinner fa-spin' />;
          }
          if (isCompleted(row.original.processingServices)) {
            return <i className='fal fa-check-circle text-success' />;
          }
          return <i className='fal fa-question-circle' />;
        }
        return (
          <Button
            color='link-secondary'
            onClick={() => setSelectedDocument({ document: row.original, mode: DocumentViewMode.Processing })}
          >
            {getIcon()}
          </Button>
        );
      },
    },
    {
      id: 'action-buttons',
      header: '',
      meta: {
        headerProps: {
          style: {
            width: 50,
          },
        },
      },
      cell: ({ row }) => {
        const document = row.original;
        return (
          <UncontrolledDropdown>
            <DropdownToggle disabled={selectedDocuments.length > 0} color='transparent' outline className='px-2'>
              <i className='fas fa-bars' />
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={() => setSelectedDocument({ document, mode: DocumentViewMode.Edit })}>
                <i className='fal fa-pencil mr-2' />
                Edit
              </DropdownItem>
              {row.original.aiProcessing === AiProcessingValue.Exclude ? (
                <DropdownItem onClick={() => toggleAiProcessing(document, AiProcessingValue.Include)}>
                  <i className='fal fa-stars mr-2' aria-hidden='true' />
                  Allow AI access
                </DropdownItem>
              ) : (
                <DropdownItem onClick={() => setSelectedDocument({ document, mode: DocumentViewMode.AiProcessing })}>
                  <i className='fal fa-ban mr-2' aria-hidden='true' />
                  Block AI access
                </DropdownItem>
              )}
              <DropdownItem onClick={() => handleDownloadDocument(document)}>
                <i className='fal fa-cloud-arrow-down mr-2' aria-hidden='true' />
                Download
              </DropdownItem>
              <DropdownItem onClick={() => setSelectedDocument({ document, mode: DocumentViewMode.Delete })}>
                <i className='fal fa-trash mr-2 text-danger' aria-hidden='true' />
                Delete
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        )
      },
    },
  ];

  const filteredRows: DocumentItem[] = useMemo(() => {
    const { searchText, type } = filters;
    let rows = listDocuments ?? [];
    if (searchText) {
      const searchIndex = generateDocumentsFlexSearchMap(listDocuments);
      const result = searchIndex.search(searchText);
      const matchedIds = new Set(result.map((item) => item.result).flat());
      rows = rows.filter((doc) => matchedIds.has(doc._id));
    }
    if (type) {
      rows = rows.filter((doc) => doc.ownerSubType === type);
    }
    return rows;
  }, [filters, listDocuments]);

  const handleChangeFilters = (key: keyof FilterParams, value?: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleUpdateDocument = (data: DocumentItem) => {
    updateDocument({ initiativeId, documentId: data._id, data })
      .unwrap()
      .then(() => {
        setIsPolling(true);
        generateToast({
          color: 'success',
          icon: 'fal fa-check-circle',
          message: 'Document has been updated',
        });
      })
      .catch((error) => {
        generateErrorToast({
          message: error?.message || 'Failed to update document',
        });
      });
  };

  const handleBlockAi = (document: DocumentItem) => {
    return toggleAiProcessing(document, AiProcessingValue.Exclude);
  };

  const toggleAiProcessing = (document: DocumentItem, aiProcessing: AiProcessingValue) => {
    updateDocument({
      initiativeId,
      documentId: document._id,
      data: { aiProcessing },
    })
      .unwrap()
      .then(() => {
        generateToast({
          color: 'success',
          message: `AI access ${aiProcessing === AiProcessingValue.Include ? 'allowed' : 'blocked'}`,
        });
      })
      .catch((error) => {
        generateErrorToast({
          message: error?.message || 'Failed to update AI access',
        });
      });
  };

  const handleDownloadDocument = (document: DocumentItem) => {
    if (!document.url) {
      generateErrorToast({
        message: error?.message || 'Document URL not found',
      });
      return;
    }
    window.open(document.url, '_blank');
  };

  const handleDeleteDocument = (deletingDocumentId?: string) => {
    if (!deletingDocumentId) {
      return;
    }
    deleteDocument({ initiativeId, documentId: deletingDocumentId })
      .unwrap()
      .then(() => {
        generateToast({
          color: 'success',
          icon: 'fal fa-check-circle',
          message: 'Document deleted',
        });
      })
      .catch((error) => {
        generateErrorToast({
          message: error?.message || 'Failed to delete document',
        });
      })
      .finally(() => {
        setSelectedDocument(undefined);
        setSelectedDocuments([]);
      });
  };

  return (
    <>
      {error ? <BasicAlert type='danger'>{error.message}</BasicAlert> : null}
      {isLoading || isFetching ? <BlockingLoader /> : null}
      {listDocuments.length === 0 && !isFetching ? (
        <div className='text-center'>
          <h1 className='text-ThemeTextPlaceholder mb-5'>No documents uploaded yet!</h1>
          <img src={NoDocuments} alt='no documents in library' />
        </div>
      ) : (
        <>
          <DocumentFilters filters={filters} onChangeFilters={handleChangeFilters} />
          <div data-testid='manage-documents-table'>
            <Table
              pageSize={10}
              columns={columns}
              data={filteredRows}
              className='mt-3'
              responsive
              noData={<BasicAlert type='secondary'>{isFetching ? 'Loading...' : 'No documents found'}</BasicAlert>}
            />
          </div>
        </>
      )}
      <DocumentToolbar
        initiativeId={initiativeId}
        selectedDocuments={selectedDocuments}
        setSelectedDocuments={setSelectedDocuments}
      />
      {isModalOpen(selectedDocument) ? (
        <DocumentModal
          isOpen={true}
          mode={selectedDocument.mode}
          document={selectedDocument.document}
          toggle={() => setSelectedDocument(undefined)}
          handleUpdate={handleUpdateDocument}
          handleDelete={handleDeleteDocument}
          handleBlockAi={handleBlockAi}
        />
      ) : null}
    </>
  );
};
